import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';

/**
 * The card the anchored actions are drawn on, in both of its shapes: a pill of
 * round buttons, and the list that unfolds from it.
 *
 * Translucent and blurred because it sits *on the wall* rather than beside it —
 * it covers the holds under it while it is up, and a setter has to be able to
 * see roughly what they are about to lose the sight of. Shadowed for the same
 * reason: on a photograph nothing else says the panel is above the picture.
 */
export function PopoverPanel({
  className,
  testId,
  children,
}: {
  className?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex flex-col gap-0.5 rounded-2xl border border-border bg-background/95 p-1 shadow-xl backdrop-blur',
        className
      )}
    >
      {children}
    </div>
  );
}

/** One of the round icon-only buttons in the small row. */
export function PopoverRoundButton({
  label,
  onClick,
  disabled = false,
  pressed,
  testId,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** For a button that opens and shuts something: whether it is open. */
  pressed?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      data-testid={testId}
      className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 aria-pressed:bg-muted aria-pressed:text-foreground"
    >
      {children}
    </button>
  );
}

/** One named action in the unfolded list, its icon on the left. */
export function PopoverRow({
  label,
  icon,
  onClick,
  active = false,
  destructive = false,
  disabled = false,
  title,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** The answer this list currently stands at, for a row that is a choice. */
  active?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active || undefined}
      aria-label={label}
      title={title ?? label}
      className={cn(
        'flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        active ? 'bg-muted font-medium' : 'hover:bg-muted/60',
        destructive && 'text-destructive'
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}
